import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './work-order-viewer.style.js';
import { 
  getAssetsByCompany, 
  getUsersByCompany, 
  getAssets,
  getUsers,
  updateWorkOrder, 
  getDowntimesByWorkOrder, 
  updateDowntime,
  getPartsByAsset,
  getPartUsagesByWorkOrder,
  usePartInWorkOrder,
  deletePartUsage,
  createDowntime
} from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { WorkOrderStatusCat, WorkOrderPriorityCat, WorkOrderModel } from '@cmms/models';
import { WorkOrder, WorkOrderFormData } from "../../../types/work-order.types.js";
import { Part } from "../../../types/part.types.js";
import { PartUsage, PartUsageFormData } from "../../../types/part-usage.types.js";
@customElement('work-order-viewer')
export class WorkOrderViewer extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) workOrder: WorkOrder | null = null;
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private assets: any[] = [];
  @state() private users: any[] = [];
  @state() private form: WorkOrderFormData = {} as any;
  @state() private original: WorkOrderFormData = {} as any;
  @state() private isSaving = false;
  @state() private assetIsDown = false;
  @state() private hasActiveDowntime = false;
  @state() private activeDowntimeId: string | null = null;
  @state() private validationError = '';
  
  // Parts usage state
  @state() private selectedTab = 'details';
  @state() private assetParts: Part[] = [];
  @state() private partsUsage: PartUsage[] = [];
  @state() private partsLoading = false;
  @state() private partUsageForm: PartUsageFormData = { part_id: '', quantity: 1 };
  @state() private addingPartUsage = false;

  // Computed property to check if work order is completed
  private get isCompleted() {
    return this.workOrder?.status_cat === WorkOrderStatusCat.COMPLETED;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadAssetsAndUsers();
    this.loadDowntime();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('workOrder') && this.workOrder) {
      this.initializeForm();
    }
    
    // Re-initialize form when assets/users are loaded if we have a work order
    if ((changed.has('assets') || changed.has('users')) && this.workOrder && this.assets.length > 0 && this.users.length > 0) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    if (!this.workOrder) return;
    
    const wo = this.workOrder;
    
    // Handle both direct properties and nested model properties
    const getValue = (field: string) => {
      // Try direct access first
      if ((wo as any)[field] !== undefined) {
        return (wo as any)[field];
      }
      // Try nested model access
      if ((wo as any).model && (wo as any).model[field] !== undefined) {
        return (wo as any).model[field];
      }
      return undefined;
    };
    
    this.form = {
      description: getValue('description') ?? '',
      status_cat: getValue('status_cat') ?? WorkOrderStatusCat.OPEN,
      priority_cat: getValue('priority_cat') ?? WorkOrderPriorityCat.MEDIUM,
      asset_id: getValue('asset_id') != null ? String(getValue('asset_id')) : '',
      assigned_to: getValue('assigned_to') != null ? String(getValue('assigned_to')) : '',
      
      created_at: getValue('created_at') ?? Date.now()
    } as any;
    this.original = JSON.parse(JSON.stringify(this.form));
    
    // Force select components to update their display values
    setTimeout(() => {
      this.forceSelectUpdates();
    }, 100);
    
    this.loadDowntime();
  }

  private forceSelectUpdates() {
    // Find all sl-select elements
    const selects = this.shadowRoot?.querySelectorAll('sl-select') as NodeListOf<any>;
    
    if (selects) {
      // The asset select should be the 3rd select (after status and priority)
      const assetSelect = selects[2];
      if (assetSelect) {
        assetSelect.value = this.form.asset_id;
      }
      
      // The assigned-to select should be the 4th select (after status, priority, and asset)
      const assignedSelect = selects[3];
      if (assignedSelect) {
        assignedSelect.value = this.form.assigned_to;
      }
    }
  }

  private async loadAssetsAndUsers() {
    try {
      const assetsPromise = this.companyId
        ? getAssetsByCompany(this.firestore, this.companyId)
        : getAssets(this.firestore);
      const usersPromise = this.companyId
        ? getUsersByCompany(this.firestore, this.companyId)
        : getUsers(this.firestore);

      const [assetsResult, usersResult] = await Promise.all([assetsPromise, usersPromise]);
      this.assets = (assetsResult as any)?.data ?? (Array.isArray(assetsResult) ? (assetsResult as any) : []);
      this.users = (usersResult as any)?.data ?? (Array.isArray(usersResult) ? (usersResult as any) : []);
      
    } catch (error) {
      console.error('Error loading assets or users:', error);
    }
  }

  private async loadDowntime() {
    if (!this.workOrder?.id) return;
    
    try {
      const downtimesResult = await getDowntimesByWorkOrder(this.firestore, this.workOrder.id, this.companyId); 
      if (downtimesResult.data && Array.isArray(downtimesResult.data)) {
        // Check if there's an active downtime (ended_at is null)
        const activeDowntime = downtimesResult.data.find((dt: any) => 
          dt.model?.ended_at === null || dt.model?.isActive?.()
        );
        this.hasActiveDowntime = !!activeDowntime;
        this.assetIsDown = !!activeDowntime; // Check if asset is down (has active downtime)
        this.activeDowntimeId = activeDowntime ? activeDowntime.id : null;
      }
    } catch { 
      this.hasActiveDowntime = false;
    }
  }

  private isDirty() {
    // Check each form field individually for better debugging
    for (const key of Object.keys(this.form)) {
      if ((this.form as any)[key] !== (this.original as any)[key]) {
        return true;
      }
    }
    // Also check for downtime changes
    return this.hasDowntimeChanges();
  }

  private closeModal = () => {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
    this.open = false;
  };

  private formatDateForInput(ts: number | null) {
    if (!ts) return '';
    const d = new Date(ts);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  }

  private parseDateInput(val: string): number | null {
    if (!val) return null;
    const t = new Date(val).getTime();
    return Number.isNaN(t) ? null : t;
  }

  private async handleDone() {
    if (!this.workOrder) { this.closeModal(); return; }
    
    // If work order is completed, just close the modal without saving
    if (this.isCompleted) {
      this.closeModal();
      return;
    }
    
    // Validation: Check if trying to complete work order while asset is still down
    const isBeingCompleted = this.form.status_cat === WorkOrderStatusCat.COMPLETED; // Status 3 is "Completed"
    if (isBeingCompleted && this.assetIsDown) {
      this.validationError = "You can't complete work order while the asset is still down. Please uncheck 'Asset is down' first.";
      return;
    }
    
    this.validationError = ''; // Clear any previous errors
    
    // Check if we have any changes to save
    const hasFormChanges = this.isDirty();
    const hasDowntimeChanges = this.hasDowntimeChanges();
    
    if (!hasFormChanges && !hasDowntimeChanges) { 
      this.closeModal(); 
      return; 
    }

    try {
      this.isSaving = true;
      
      // Handle downtime changes first
      if (hasDowntimeChanges) {
        await this.handleDowntimeChanges();
      }
      
      // Handle work order updates
      if (hasFormChanges) {
        const update: any = {};
        for (const k of Object.keys(this.form) as (keyof WorkOrderFormData)[]) {
          if ((this.form as any)[k] !== (this.original as any)[k]) {
            update[k] = (this.form as any)[k];
          }
        }

        // Timing adjustments when status changes
        const origStatus = (this.original as any).status_cat as number | undefined;
        const nextStatus = (this.form as any).status_cat as number | undefined;
        const now = Date.now();
        if (typeof origStatus === 'number' && typeof nextStatus === 'number' && origStatus !== nextStatus) {
          const wasInProgress = origStatus === WorkOrderStatusCat.IN_PROGRESS;
          const willBeInProgress = nextStatus === WorkOrderStatusCat.IN_PROGRESS;

          const currentStartedAt = (this.workOrder as any)?.current_started_at ?? null;
          let activeWorkMs = (this.workOrder as any)?.active_work_ms ?? 0;

          // Leaving IN_PROGRESS → roll up
          if (wasInProgress && !willBeInProgress) {
            if (typeof currentStartedAt === 'number') {
              const delta = Math.max(0, now - currentStartedAt);
              activeWorkMs = Math.max(0, activeWorkMs + delta);
              update.current_started_at = null;
              update.active_work_ms = activeWorkMs;
            }
          }
          // Entering IN_PROGRESS → start session if none
          if (!wasInProgress && willBeInProgress) {
            if (currentStartedAt == null) {
              update.current_started_at = now;
            }
          }
          // Completing → ensure roll-up and set completed_at
          if (nextStatus === WorkOrderStatusCat.COMPLETED) {
            if (typeof currentStartedAt === 'number') {
              const delta = Math.max(0, now - currentStartedAt);
              activeWorkMs = Math.max(0, activeWorkMs + delta);
              update.current_started_at = null;
              update.active_work_ms = activeWorkMs;
            }
            update.completed_at = now;
          }
        }
        
        if (Object.keys(update).length) {
          update.updated_at = Date.now();
          await updateWorkOrder(this.firestore, this.workOrder.id, update);
          this.dispatchEvent(new CustomEvent('work-order-updated', { bubbles: true, composed: true }));
        }
      }
      
      this.closeModal();
    } finally {
      this.isSaving = false;
    }
  }

  private hasDowntimeChanges(): boolean {
    // Check if downtime status has changed
    // Case 1: Had active downtime but now unchecked (need to end downtime)
    // Case 2: No active downtime but now checked (need to create downtime)
    return (this.hasActiveDowntime && !this.assetIsDown) || (!this.hasActiveDowntime && this.assetIsDown);
  }

  private async handleDowntimeChanges() {
    // If asset was down but now checkbox is unchecked, end the downtime
    if (this.hasActiveDowntime && !this.assetIsDown && this.activeDowntimeId) {
      await updateDowntime(this.firestore, this.activeDowntimeId, {
        ended_at: Date.now()
      });
    }
    // If no active downtime but checkbox is now checked, create new downtime
    else if (!this.hasActiveDowntime && this.assetIsDown && this.workOrder?.asset_id) {
      // Check if there are any existing downtimes for this work order (even completed ones)
      try {
        const existingDowntimesResult = await getDowntimesByWorkOrder(this.firestore, this.workOrder.id, this.companyId);
        const existingDowntimes = existingDowntimesResult.data || [];
        
        if (existingDowntimes.length > 0) {
          this.validationError = "This work order already has downtime records. Please create a new work order for additional downtime tracking.";
          return;
        }
        
        // Get the Firebase document ID
        const firebaseDocId = (this.workOrder as any).$id || 
                             (this.workOrder as any).wo_id || 
                             this.workOrder.id || 
                             (this.workOrder as any).firebaseId;
        
        await createDowntime(this.firestore, {
          asset_id: this.workOrder.asset_id,
          wo_id: firebaseDocId,
          started_at: Date.now(),
          ended_at: null,
          company_id: this.companyId
        });
      } catch (error) {
        console.error('Error checking existing downtimes:', error);
        this.validationError = 'Failed to check existing downtime records';
      }
    }
  }

  // Parts usage methods
  private async loadAssetParts() {
    if (!this.workOrder?.asset_id) return;
    
    try {
      this.partsLoading = true;
      const result = await getPartsByAsset(this.firestore, this.workOrder.asset_id, this.companyId);
      if (result.error) {
        console.error('Error loading asset parts:', result.error);
        this.assetParts = [];
      } else {
        this.assetParts = result.data || [];
      }
    } catch (error) {
      console.error('Error loading asset parts:', error);
      this.assetParts = [];
    } finally {
      this.partsLoading = false;
    }
  }

  private async loadPartsUsage() {
    if (!this.workOrder?.id) return;
    
    try {
      const result = await getPartUsagesByWorkOrder(this.firestore, this.workOrder.id, this.companyId);
      if (result.error) {
        console.error('Error loading parts usage:', result.error);
        this.partsUsage = [];
      } else {
        this.partsUsage = result.data || [];
      }
    } catch (error) {
      console.error('Error loading parts usage:', error);
      this.partsUsage = [];
    }
  }

  private async handleTabChange(event: any) {
    this.selectedTab = event.detail.name;
    if (event.detail.name === 'parts') {
      await Promise.all([
        this.loadAssetParts(),
        this.loadPartsUsage()
      ]);
    }
  }

  private async handleAddPartUsage() {
    if (!this.partUsageForm.part_id || !this.partUsageForm.quantity || !this.workOrder) {
      return;
    }

    try {
      this.addingPartUsage = true;
      const result = await usePartInWorkOrder(this.firestore, {
        work_order_id: this.workOrder.id,
        part_id: this.partUsageForm.part_id,
        quantity: this.partUsageForm.quantity,
        company_id: this.companyId
      });

      if (result.error) {
        this.validationError = result.error;
        return;
      }

      // Reset form and reload data
      this.partUsageForm = { part_id: '', quantity: 1 };
      this.validationError = '';
      
      // Reload both parts (to update stock) and usage history
      await Promise.all([
        this.loadAssetParts(),
        this.loadPartsUsage()
      ]);

    } catch (error) {
      console.error('Error adding part usage:', error);
      this.validationError = 'Failed to add part usage';
    } finally {
      this.addingPartUsage = false;
    }
  }

  private async handleDeletePartUsage(partUsageId: string) {
    try {
      const result = await deletePartUsage(this.firestore, partUsageId);
      if (result.error) {
        console.error('Error deleting part usage:', result.error);
        return;
      }

      // Reload usage history
      await this.loadPartsUsage();
    } catch (error) {
      console.error('Error deleting part usage:', error);
    }
  }

  // Table configuration methods
  private createAssetPartsTableConfig(): any {
    return {
      data: this.assetParts,
      loading: this.partsLoading,
      title: 'Available Parts',
      columns: [
        {
          key: 'name',
          label: 'Part Name',
          type: 'text',
          sortable: true
        },
        {
          key: 'description', 
          label: 'Description',
          type: 'text',
          sortable: true
        },
        {
          key: 'stock_qty',
          label: 'Stock',
          type: 'text',
          sortable: true,
          formatter: (value: number) => `${value} units`
        },
        {
          key: 'cost',
          label: 'Unit Cost',
          type: 'text',
          sortable: true,
          formatter: (value: number) => `$${value.toFixed(2)}`
        }
      ],
      rowActions: [
        {
          label: 'Use Part',
          action: 'use-part',
          variant: 'primary',
          size: 'small'
        }
      ],
      onAction: (action: string, item: Part) => {
        if (action === 'use-part') {
          this.partUsageForm.part_id = item.id;
        }
      },
      emptyState: {
        icon: 'box',
        title: 'No Parts Available',
        message: 'No parts are associated with this asset.'
      }
    };
  }

  private createPartsUsageTableConfig(): any {
    // Enrich parts usage with part details
    const enrichedUsage = this.partsUsage.map(usage => {
      const part = this.assetParts.find(p => p.id === usage.part_id);
      return {
        ...usage,
        part_name: part?.name || 'Unknown Part',
        unit_cost: part?.cost || 0,
        total_cost: (part?.cost || 0) * usage.quantity
      };
    });

    return {
      data: enrichedUsage,
      loading: false,
      title: 'Parts Usage History',
      columns: [
        {
          key: 'part_name',
          label: 'Part Name',
          type: 'text',
          sortable: true
        },
        {
          key: 'quantity',
          label: 'Quantity Used',
          type: 'text',
          sortable: true,
          formatter: (value: number) => `${value} units`
        },
        {
          key: 'unit_cost',
          label: 'Unit Cost',
          type: 'text',
          sortable: true,
          formatter: (value: number) => `$${value.toFixed(2)}`
        },
        {
          key: 'total_cost',
          label: 'Total Cost',
          type: 'text',
          sortable: true,
          formatter: (value: number) => `$${value.toFixed(2)}`
        },
        {
          key: 'timestamp',
          label: 'Used At',
          type: 'date',
          sortable: true
        }
      ],
      
      defaultSort: {
        key: 'timestamp',
        direction: 'desc' as const
      },
      emptyState: {
        icon: 'tools',
        title: 'No Parts Used',
        message: 'No parts have been used in this work order yet.'
      }
    };
  }

  render() {
    if (!this.workOrder) return html``;

    return html`
      <sl-dialog label="Work Order" class="work-order-dialog-large" .open=${this.open} @sl-hide=${this.closeModal}>    
        ${this.validationError ? html`
          <sl-alert variant="danger" open>
            <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
            ${this.validationError}
          </sl-alert>
        ` : ''}

        ${this.isCompleted ? html`
          <sl-alert variant="info" open>
            <sl-icon slot="icon" name="info-circle"></sl-icon>
            This work order is completed and cannot be modified.
          </sl-alert>
        ` : ''}

        <sl-tab-group @sl-tab-show=${this.handleTabChange}>
          <sl-tab slot="nav" panel="details">Details</sl-tab>
          <sl-tab slot="nav" panel="parts">Parts Usage</sl-tab>

          <sl-tab-panel name="details">
            ${this.renderDetailsTab()}
          </sl-tab-panel>

          <sl-tab-panel name="parts">
            ${this.renderPartsTab()}
          </sl-tab-panel>
        </sl-tab-group>

        <div slot="footer">
          <sl-button
            size="small"
            variant=${this.isCompleted ? 'neutral' : (this.isDirty() ? 'primary' : 'neutral')}
            ?loading=${this.isSaving}
            @click=${this.handleDone}>
            ${this.isCompleted ? 'Close' : (this.isDirty() ? 'Save & Close' : 'Done')}
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }

  private renderDetailsTab() {
    return html`
      <form class="details-grid" @submit=${(e: Event) => { e.preventDefault(); this.handleDone(); }}>
        <div class="field-row">
          <label>Description</label>
          <sl-textarea
            .value=${this.form.description ?? ''}
            rows="3"
            resize="vertical"
            ?disabled=${this.isCompleted}
            @sl-input=${(e: any) => this.form = { ...this.form, description: e.target.value }}>
          </sl-textarea>
        </div>

        <div class="field-row">
          <label>Status</label>
          <sl-select
            .value=${String(this.form.status_cat ?? WorkOrderStatusCat.OPEN)}
            ?disabled=${this.isCompleted}
            @sl-change=${(e: any) => {
              const next = Number(e.target.value);
              this.form = { ...this.form, status_cat: next };
              this.validationError = ''; // Clear error when status changes
            }}
            @sl-hide=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-after-show=${(e: Event) => e.stopPropagation()}
            @sl-after-hide=${(e: Event) => e.stopPropagation()}>
            ${WorkOrderModel.statusCats.map(s => html`<sl-option value=${String(s.cat)}>${s.name}</sl-option>`)}
          </sl-select>
        </div>

        <div class="field-row">
          <label>Priority</label>
          <sl-select
            .value=${String(this.form.priority_cat ?? WorkOrderPriorityCat.MEDIUM)}
            ?disabled=${this.isCompleted}
            @sl-change=${(e: any) => this.form = { ...this.form, priority_cat: Number(e.target.value) }}
            @sl-hide=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-after-show=${(e: Event) => e.stopPropagation()}
            @sl-after-hide=${(e: Event) => e.stopPropagation()}>
            ${WorkOrderModel.priorityCats.map(p => html`<sl-option value=${String(p.cat)}>${p.name}</sl-option>`)}
          </sl-select>
        </div>

        <div class="field-row">
          <label>Asset</label>
          <sl-select
            .value=${this.form.asset_id ?? ''}
            ?disabled=${this.isCompleted}
            @sl-change=${(e: any) => this.form = { ...this.form, asset_id: e.target.value }}
            @sl-hide=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-after-show=${(e: Event) => e.stopPropagation()}
            @sl-after-hide=${(e: Event) => e.stopPropagation()}>
            <sl-option value="">No asset</sl-option>
            ${this.assets.map(a => html`<sl-option value=${String(a.id)}>${a.name || a.model?.name || a.id}</sl-option>`)}
          </sl-select>
        </div>

        <div class="field-row">
          <label>Assigned To</label>
          <sl-select
            .value=${this.form.assigned_to ?? ''}
            ?disabled=${this.isCompleted}
            @sl-change=${(e: any) => this.form = { ...this.form, assigned_to: e.target.value }}
            @sl-hide=${(e: Event) => e.stopPropagation()}
            @sl-show=${(e: Event) => e.stopPropagation()}
            @sl-after-show=${(e: Event) => e.stopPropagation()}
            @sl-after-hide=${(e: Event) => e.stopPropagation()}>
            <sl-option value="">Unassigned</sl-option>
            ${this.users.map(u => html`<sl-option value=${String(u.id)}>${u.model?.name || u.model?.email}</sl-option>`)}
          </sl-select>
        </div>

        <div class="field-row">
          <label>Downtime</label>
          <sl-checkbox 
            ?checked=${this.assetIsDown}
            ?disabled=${this.isCompleted}
            @sl-change=${(e: any) => {
              this.assetIsDown = e.target.checked;
              this.validationError = ''; // Clear error when checkbox changes
            }}>
            Asset is down
          </sl-checkbox>
        </div>
      </form>
    `;
  }

  private renderPartsTab() {
    const selectedPart = this.assetParts.find(p => p.id === this.partUsageForm.part_id);
    const totalCost = selectedPart ? selectedPart.cost * this.partUsageForm.quantity : 0;

    return html`
      <div class="parts-usage-container">
        ${this.isCompleted ? html`
          <sl-alert variant="info" open>
            <sl-icon slot="icon" name="info-circle"></sl-icon>
            Parts usage cannot be modified for completed work orders.
          </sl-alert>
        ` : ''}
        
        <!-- Add Part Usage Form -->
        <div class="add-part-section">
          <h4>Use Part</h4>
          <div class="add-part-form">
            <sl-select
              label="Select Part"
              placeholder="Choose a part..."
              .value=${this.partUsageForm.part_id}
              ?disabled=${this.isCompleted}
              @sl-change=${(e: any) => {
                this.partUsageForm = { ...this.partUsageForm, part_id: e.target.value };
              }}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-after-show=${(e: Event) => e.stopPropagation()}
              @sl-after-hide=${(e: Event) => e.stopPropagation()}>
              <sl-option value="">Select a part...</sl-option>
              ${this.assetParts.map(part => html`
                <sl-option value=${part.id}>
                  ${part.name} (Stock: ${part.stock_qty}, Cost: $${part.cost.toFixed(2)})
                </sl-option>
              `)}
            </sl-select>

            <sl-input
              type="number"
              label="Quantity"
              min="1"
              .value=${String(this.partUsageForm.quantity)}
              ?disabled=${this.isCompleted}
              @sl-input=${(e: any) => {
                this.partUsageForm = { ...this.partUsageForm, quantity: Number(e.target.value) };
              }}>
            </sl-input>

            ${selectedPart ? html`
              <div class="cost-preview">
                <p><strong>Unit Cost:</strong> $${selectedPart.cost.toFixed(2)}</p>
                <p><strong>Total Cost:</strong> $${totalCost.toFixed(2)}</p>
                <p><strong>Available Stock:</strong> ${selectedPart.stock_qty} units</p>
                ${selectedPart.stock_qty < this.partUsageForm.quantity ? html`
                  <sl-alert variant="warning" open>
                    <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                    Insufficient stock! Only ${selectedPart.stock_qty} units available.
                  </sl-alert>
                ` : ''}
              </div>
            ` : ''}

            <sl-button
              variant="primary"
              ?loading=${this.addingPartUsage}
              ?disabled=${this.isCompleted || !this.partUsageForm.part_id || this.partUsageForm.quantity < 1 || (selectedPart && selectedPart.stock_qty < this.partUsageForm.quantity)}
              @click=${this.handleAddPartUsage}>
              Use Part
            </sl-button>
          </div>
        </div>

        <!-- Parts Usage History Table -->
        <div class="parts-usage-section">
          <data-table .config=${this.createPartsUsageTableConfig()}></data-table>
        </div>
      </div>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'work-order-viewer': WorkOrderViewer;
  }
}
